import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to control GIF animation replay on viewport visibility
 * 
 * This hook manages a GIF that should replay its animation when it comes back into view:
 * - The GIF plays once on initial render
 * - When user scrolls down and the GIF leaves the viewport, it's marked as not visible
 * - When user scrolls back up and the GIF re-enters the viewport, the animation replays
 * - Prevents multiple simultaneous reloads with a debounce flag
 * 
 * @param {React.RefObject} imageRef - Reference to the image element to observe
 * @returns {number} gifKey - A key that increments to force GIF re-render and replay animation
 * 
 * @example
 * const imageRef = useRef(null);
 * const gifKey = useGifReplay(imageRef);
 * 
 * return (
 *   <img 
 *     ref={imageRef} 
 *     key={gifKey} 
 *     src={`${myGif}?t=${gifKey}`} 
 *     alt="Animated GIF"
 *   />
 * );
 */
const useGifReplay = (imageRef) => {
  // Track if the GIF was previously visible in the viewport
  const wasVisibleRef = useRef(true);
  // Prevent multiple simultaneous reload triggers
  const isReloadingRef = useRef(false);
  // Key that changes to force React to re-render the image, replaying the GIF
  const [gifKey, setGifKey] = useState(0);

  useEffect(() => {
    const imageElement = imageRef.current;
    if (!imageElement) return;

    // Set up IntersectionObserver to watch when the GIF enters/leaves viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // GIF just became visible AND wasn't visible before AND not currently reloading
          if (entry.isIntersecting && !wasVisibleRef.current && !isReloadingRef.current) {
            // Set flags to prevent duplicate triggers
            isReloadingRef.current = true;
            wasVisibleRef.current = true;
            
            // Increment key to force re-render and replay animation
            setGifKey((prevKey) => prevKey + 1);
            
            // Reset reload flag after a brief delay
            setTimeout(() => {
              isReloadingRef.current = false;
            }, 100);
          } 
          // GIF left the viewport - mark as not visible (ready for next replay)
          else if (!entry.isIntersecting && wasVisibleRef.current && !isReloadingRef.current) {
            wasVisibleRef.current = false;
          }
        });
      },
      {
        // Trigger when 10% of the image is visible
        threshold: 0.1,
      }
    );

    observer.observe(imageElement);

    // Cleanup observer on unmount
    return () => {
      if (imageElement) {
        observer.unobserve(imageElement);
      }
    };
  }, [imageRef, gifKey]); // Re-run when imageRef or gifKey changes

  return gifKey;
};

export default useGifReplay;
